/**
 * EditorShareMenu — the share control that sits next to the connection
 * badge (the "wifi" icon) in every editor toolbar's status slot.
 *
 * Clicking the Share icon opens a small dropdown with the two share
 * flows the drive already exposes on file rows:
 *   - "Share to another Drive" → ShareDialog (cross-org `.shared` mount)
 *   - "Share link"            → ShareLinksDialog (unified link manager)
 *
 * The dropdown portals via ToolbarPopover so it escapes the toolbar /
 * editor-card clip. Every editor knows its own `fileId`/`fileName`, so
 * they just drop <EditorShareMenu fileId=… fileName=… /> before the
 * <CollabStatusBadge/> in their status cluster.
 */
import { useRef, useState } from 'react'
import { Share, Share2 } from 'lucide-react'
import { ShareDialog } from '@papan/org-connections/ui'
import { ShareLinksDialog, useMiniAppShare } from '@papan/share-links/ui'
import { shareKindForFile, canShareMiniApp, miniappAncestors } from '@papan/share-links/client'
import { useBreadcrumbs } from '@papan/files/client/queries'
import { IconButton } from './ToolbarControls.js'
import { ToolbarPopover } from './ToolbarPopover.js'
import { PDAppIcon } from '@papan/core-ui/ui'

export function EditorShareMenu({
  fileId,
  fileName,
  isFolder = false,
}: {
  fileId: string
  fileName?: string
  isFolder?: boolean
}) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialog, setDialog] = useState<null | 'drive' | 'link'>(null)
  const name = fileName || fileId
  // This editor's file: is it an HTML page inside a `.miniapp`? Breadcrumbs are
  // cached (SingleFileView seeds them via the file-detail endpoint), so this is a
  // cache read, not a fetch. `canShareMiniApp` gates on HTML-file-in-.miniapp.
  const { data: crumbs = [] } = useBreadcrumbs(fileId)
  const miniApp = useMiniAppShare()
  const showMiniApp = canShareMiniApp({ name, isFolder }, crumbs)

  return (
    <>
      <div ref={anchorRef} className="relative flex items-center">
        <IconButton
          icon={<Share size={14} />}
          label="Share"
          className="ws-tool-toggle"
          data-on={menuOpen ? '' : undefined}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(v => !v)}
        />
        <ToolbarPopover
          anchorRef={anchorRef}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          align="right"
          className="ws-menu"
        >
          <button type="button" className="ws-menu-item" onClick={() => { setMenuOpen(false); setDialog('drive') }}>
            <Share2 size={14} className="shrink-0" />
            Share to another Drive
          </button>
          <button type="button" className="ws-menu-item" onClick={() => { setMenuOpen(false); setDialog('link') }}>
            <Share2 size={14} className="shrink-0" />
            Share link
          </button>
          {showMiniApp && (
            <button type="button" className="ws-menu-item" onClick={() => { setMenuOpen(false); miniApp.openMiniAppShare(miniappAncestors(crumbs)) }}>
              <PDAppIcon size={14} className="shrink-0 text-pink-500" />
              Share PDApp
            </button>
          )}
        </ToolbarPopover>
      </div>
      {miniApp.node}

      {dialog === 'drive' && (
        <ShareDialog
          folderId={fileId}
          folderName={name}
          isFolder={isFolder}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'link' && (
        <ShareLinksDialog
          fileId={fileId}
          fileName={name}
          isFolder={isFolder}
          isAppEligible={shareKindForFile({ name, isFolder }) === 'app'}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  )
}
